import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { Download, LogOut, Search, Loader2, Inbox, Trash2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatApiErrorDetail = (detail) => {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
};

const TABS = [
  { key: "rental", label: "Rental List", group: "interest", match: (r) => r.list_type === "rental" },
  { key: "purchase", label: "Purchase List", group: "interest", match: (r) => r.list_type === "purchase" },
  { key: "pledges", label: "Pledges", group: "pledges", match: () => true },
  { key: "messages", label: "Messages", group: "messages", match: () => true },
];

const COLUMNS = {
  rental: [
    ["name", "Name"],
    ["email", "Email"],
    ["phone", "Phone"],
    ["message", "Note"],
    ["created_at", "Joined"],
  ],
  purchase: [
    ["name", "Name"],
    ["email", "Email"],
    ["phone", "Phone"],
    ["message", "Note"],
    ["created_at", "Joined"],
  ],
  pledges: [
    ["name", "Name"],
    ["email", "Email"],
    ["amount", "Amount"],
    ["frequency", "Frequency"],
    ["message", "Note"],
    ["created_at", "Date"],
  ],
  messages: [
    ["name", "Name"],
    ["email", "Email"],
    ["subject", "Subject"],
    ["message", "Message"],
    ["created_at", "Date"],
  ],
};

const fmtDate = (v) => {
  if (!v) return "";
  const d = new Date(v);
  return isNaN(d) ? String(v) : d.toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
};

const fmtCell = (key, val) => {
  if (val == null || val === "") return "—";
  if (key === "created_at") return fmtDate(val);
  if (key === "amount") return `$${Number(val).toLocaleString()}`;
  return String(val);
};

const AdminPage = () => {
  const [user, setUser] = useState(undefined);
  const [loginForm, setLoginForm] = useState({ email: "", password: "" });
  const [loginError, setLoginError] = useState("");
  const [loginLoading, setLoginLoading] = useState(false);
  const [data, setData] = useState({ interest: [], pledges: [], messages: [] });
  const [tab, setTab] = useState("rental");
  const [query, setQuery] = useState("");
  const [loadingData, setLoadingData] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const activeTab = TABS.find((t) => t.key === tab);

  const loadSubmissions = async () => {
    setLoadingData(true);
    try {
      const res = await axios.get(`${API}/admin/submissions`, { withCredentials: true });
      setData(res.data);
    } catch (e) {
      if (e.response?.status === 401) setUser(null);
      else toast.error("Could not load submissions.");
    } finally {
      setLoadingData(false);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/auth/me`, { withCredentials: true });
        setUser(res.data);
        loadSubmissions();
      } catch {
        setUser(null);
      }
    })();
  }, []);

  const login = async (e) => {
    e.preventDefault();
    setLoginLoading(true);
    setLoginError("");
    try {
      const { data: u } = await axios.post(`${API}/auth/login`, loginForm, { withCredentials: true });
      setUser(u);
      loadSubmissions();
    } catch (err) {
      setLoginError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoginLoading(false);
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch {}
    setUser(null);
  };

  const deleteRow = async (row) => {
    if (!window.confirm(`Delete the submission from ${row.name || row.email}? This cannot be undone.`)) return;
    setDeletingId(row.id);
    try {
      await axios.delete(`${API}/admin/submissions/${activeTab.group}/${row.id}`, { withCredentials: true });
      setData((prev) => ({
        ...prev,
        [activeTab.group]: prev[activeTab.group].filter((r) => r.id !== row.id),
      }));
      toast.success("Submission deleted.");
    } catch (e) {
      if (e.response?.status === 401) setUser(null);
      else toast.error("Could not delete. Please try again.");
    } finally {
      setDeletingId(null);
    }
  };

  const tabRows = useMemo(() => (data[activeTab.group] || []).filter(activeTab.match), [data, activeTab]);

  const counts = useMemo(() => {
    const c = {};
    TABS.forEach((t) => {
      c[t.key] = (data[t.group] || []).filter(t.match).length;
    });
    return c;
  }, [data]);

  const filtered = useMemo(() => {
    if (!query.trim()) return tabRows;
    const q = query.toLowerCase();
    return tabRows.filter((r) => Object.values(r).some((v) => String(v ?? "").toLowerCase().includes(q)));
  }, [tabRows, query]);

  const downloadCSV = () => {
    const cols = COLUMNS[tab];
    const header = cols.map(([, label]) => `"${label}"`).join(",");
    const lines = filtered.map((row) =>
      cols
        .map(([key]) => {
          const v = fmtCell(key, row[key]);
          return `"${(v === "—" ? "" : v).replaceAll('"', '""')}"`;
        })
        .join(",")
    );
    const csv = [header, ...lines].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `tns-${tab}-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Spreadsheet downloaded.");
  };

  if (user === undefined) {
    return (
      <div className="min-h-screen bg-bone flex items-center justify-center">
        <Loader2 className="animate-spin text-primary" size={28} />
      </div>
    );
  }

  if (user === null) {
    return (
      <div className="min-h-screen bg-bone noise flex items-center justify-center px-6" data-testid="admin-login">
        <Toaster position="bottom-right" richColors />
        <div className="w-full max-w-md bg-card border border-foreground/10 rounded-3xl p-10">
          <div className="flex items-center gap-3 mb-8">
            <img src="/icon.png" alt="TNS Housing Corp" className="h-10 w-10 rounded-lg" />
            <p className="text-sm font-semibold tracking-[0.12em] text-foreground">
              THE NEXT <span className="text-primary">STEP</span> HOUSING CORP
            </p>
          </div>
          <h1 className="font-serif font-light text-3xl mb-2">Admin sign in</h1>
          <p className="text-sm text-muted-foreground mb-8">Submissions dashboard for the TNS team.</p>
          <form onSubmit={login} className="space-y-6" data-testid="admin-login-form">
            <input
              type="email"
              placeholder="Email address"
              value={loginForm.email}
              onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
              data-testid="admin-email-input"
              className="field-input"
              autoComplete="email"
            />
            <input
              type="password"
              placeholder="Password"
              value={loginForm.password}
              onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
              data-testid="admin-password-input"
              className="field-input"
              autoComplete="current-password"
            />
            {loginError && (
              <p className="text-sm text-red-600" data-testid="admin-login-error">{loginError}</p>
            )}
            <button
              type="submit"
              disabled={loginLoading}
              data-testid="admin-login-submit"
              className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-primary px-8 py-3.5 text-sm font-medium text-primary-foreground transition-[background-color,transform] duration-300 hover:bg-lavender-deep hover:-translate-y-0.5 disabled:opacity-60"
            >
              {loginLoading && <Loader2 size={16} className="animate-spin" />}
              {loginLoading ? "Signing in…" : "Sign in"}
            </button>
          </form>
          <a href="/" className="mt-6 inline-block link-line text-xs text-muted-foreground" data-testid="admin-back-link">
            Back to the site
          </a>
        </div>
      </div>
    );
  }

  const cols = COLUMNS[tab];

  return (
    <div className="min-h-screen bg-bone" data-testid="admin-dashboard">
      <Toaster position="bottom-right" richColors />
      <header className="sticky top-0 z-40 bg-bone/85 backdrop-blur-xl border-b border-foreground/10">
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/icon.png" alt="TNS Housing Corp" className="h-9 w-9 rounded-lg" />
            <div>
              <p className="text-xs font-semibold tracking-[0.12em] text-foreground leading-tight">
                THE NEXT <span className="text-primary">STEP</span> HOUSING CORP
              </p>
              <p className="text-[11px] text-muted-foreground">Submissions dashboard</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <span className="hidden sm:block text-xs text-muted-foreground" data-testid="admin-user-email">{user.email}</span>
            <button
              onClick={logout}
              data-testid="admin-logout-btn"
              className="inline-flex items-center gap-1.5 text-xs font-medium text-foreground/70 hover:text-foreground transition-colors"
            >
              <LogOut size={14} /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-10">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10" data-testid="admin-stats">
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => { setTab(t.key); setQuery(""); }}
              data-testid={`admin-stat-${t.key}`}
              className={`text-left rounded-2xl border p-5 transition-[border-color,background-color] duration-300 ${
                tab === t.key ? "border-primary/40 bg-secondary/50" : "border-foreground/10 bg-card hover:border-primary/25"
              }`}
            >
              <p className="font-serif text-3xl lg:text-4xl text-foreground">{counts[t.key]}</p>
              <p className="text-xs uppercase tracking-[0.15em] text-muted-foreground mt-1">{t.label}</p>
            </button>
          ))}
        </div>

        <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
          <div className="flex flex-wrap gap-2" data-testid="admin-tabs">
            {TABS.map((t) => (
              <button
                key={t.key}
                onClick={() => { setTab(t.key); setQuery(""); }}
                data-testid={`admin-tab-${t.key}`}
                className={`rounded-full px-5 py-2 text-sm font-medium border transition-colors duration-300 ${
                  tab === t.key
                    ? "bg-primary border-primary text-primary-foreground"
                    : "border-foreground/20 text-foreground/70 hover:border-primary hover:text-primary"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-3">
            <div className="relative">
              <Search size={15} className="absolute left-0 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                type="text"
                placeholder="Search…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                data-testid="admin-search-input"
                className="field-input pl-7 py-2 text-sm w-52"
              />
            </div>
            <button
              onClick={downloadCSV}
              data-testid="admin-export-btn"
              className="inline-flex items-center gap-2 rounded-full border border-foreground/20 px-5 py-2 text-sm font-medium text-foreground transition-[border-color,color] duration-300 hover:border-primary hover:text-primary"
            >
              <Download size={15} /> Export CSV
            </button>
          </div>
        </div>

        <div className="bg-card border border-foreground/10 rounded-2xl overflow-hidden" data-testid="admin-table-wrap">
          {loadingData ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="animate-spin text-primary" size={24} />
            </div>
          ) : filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-muted-foreground" data-testid="admin-empty">
              <Inbox size={28} className="mb-3 text-foreground/30" />
              <p className="text-sm">No submissions here yet.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="admin-table">
                <thead>
                  <tr className="border-b border-foreground/10 text-left">
                    {cols.map(([key, label]) => (
                      <th key={key} className="px-5 py-4 text-[11px] uppercase tracking-[0.15em] text-muted-foreground font-medium whitespace-nowrap">
                        {label}
                      </th>
                    ))}
                    <th className="px-5 py-4 w-12" />
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((row, i) => (
                    <tr key={row.id || i} className="border-b border-foreground/5 last:border-0 hover:bg-secondary/30 transition-colors" data-testid={`admin-row-${i}`}>
                      {cols.map(([key]) => (
                        <td key={key} className="px-5 py-4 align-top text-foreground/80 max-w-[16rem] truncate">
                          {fmtCell(key, row[key])}
                        </td>
                      ))}
                      <td className="px-5 py-4 align-top text-right">
                        <button
                          onClick={() => deleteRow(row)}
                          disabled={deletingId === row.id}
                          data-testid={`admin-delete-${i}`}
                          title="Delete this submission"
                          className="inline-flex items-center justify-center w-8 h-8 rounded-full text-foreground/40 transition-colors duration-300 hover:bg-red-50 hover:text-red-600 disabled:opacity-50"
                        >
                          {deletingId === row.id ? <Loader2 size={15} className="animate-spin" /> : <Trash2 size={15} />}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default AdminPage;
